import * as Sentry from '@sentry/react';

/**
 * TELV1-550 — one-shot recovery for stale-chunk load failures.
 *
 * The only route chunk that matters (`ChatPage`) is content-hashed per
 * build; a respondent landing mid-deploy, with a heuristically-cached
 * index.html, or on a flaky mobile connection gets a failed dynamic
 * import and — before this module — a dead error screen on pageload
 * (Sentry TELLET-CHAT-J8/J9/J7, ~750 respondents/30d). One full reload
 * re-fetches index.html + assets and recovers every deploy-shaped
 * variant, and gives network blips a second chance.
 *
 * Design constraints (architect-reviewed):
 * - PRE-MOUNT ONLY: the listener disarms once ChatPage mounts. A
 *   mid-interview reload in an embed drops the respondent back to
 *   consent and mints a NEW conversation (iframeLocalStorage reads are
 *   sync-from-async-cache on a fresh document), so late chunk failures
 *   (e.g. the video.js vendor chunk) are reported, never reloaded.
 * - FAIL CLOSED: sessionStorage can THROW in sandboxed/cross-site
 *   iframes — chat's primary surface. Any storage failure means "do not
 *   reload" (the boundary's error screen is the safe outcome). Never
 *   use iframeLocalStorage here: its sync read of an async cache would
 *   return null on every fresh document and guarantee a reload loop.
 * - AT MOST ONE RELOAD per document, via a module-level in-flight flag,
 *   independent of storage.
 * - Vite quirk: preventDefault() makes the failed import RESOLVE with
 *   undefined, so React.lazy still throws a derived TypeError into the
 *   router boundary while the reload is in flight — the boundary checks
 *   isChunkReloadInFlight() and renders a blank holding screen without
 *   capturing. (React.lazy also permanently caches a rejected promise,
 *   so an in-place retry without reload is not possible — do not
 *   "optimize" this into one.)
 * - Outcome is reported on the NEXT load (a capture fired synchronously
 *   before reload() mostly never leaves the page): recovery is proven
 *   by ChatPage mounting with the marker present (sampled warning);
 *   a repeat failure is denied a second reload and captured fully by
 *   the boundary with chunk_error:true.
 * - Why a reload and not an in-place retry (TELV1-550 follow-up): once a
 *   module fetch has failed, the browser's module map rejects every later
 *   import() of that URL for the life of the document without touching
 *   the network, and Safari's error carries no URL to cache-bust. Only a
 *   new document gets a fresh module map.
 * - Every denied reload records WHY (`getChunkReloadDenialReason`) so the
 *   boundary can tag the residual failures in Sentry; without that the
 *   population that never recovers is indistinguishable from the one
 *   that hits a genuinely missing asset.
 */

export type ChunkReloadDenialReason =
  | 'not-armed'
  | 'in-flight'
  | 'offline'
  | 'marker-present'
  | 'storage-unavailable'
  | 'reload-threw';

const STORAGE_KEY = 'tellet_chunk_reload_pending';
const RECOVERY_SAMPLE_RATE = 0.1;

let armed = false;
let reloadInFlight = false;
let denialReason: ChunkReloadDenialReason | null = null;

const CHUNK_ERROR_SIGNATURES = [
  // Safari
  'importing a module script failed',
  // Chrome
  'failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // Vite preload helper (CSS or JS preload)
  'unable to preload css',
];

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  if (err.name === 'ChunkLoadError') return true;
  const message = typeof err.message === 'string' ? err.message : String(error);
  const lower = message.toLowerCase();
  return CHUNK_ERROR_SIGNATURES.some((sig) => lower.includes(sig));
};

export const isChunkReloadInFlight = (): boolean => reloadInFlight;

/** Why the last chunk failure was NOT auto-reloaded (null = it was, or none yet). */
export const getChunkReloadDenialReason = (): ChunkReloadDenialReason | null => denialReason;

// Fail-closed storage access: any throw (sandboxed iframe, storage
// disabled) reads as "marker present" for the guard so we never reload.
const readMarker = (): string | null | 'unavailable' => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return 'unavailable';
  }
};

const writeMarker = (): boolean => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

const clearMarker = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clean up if storage is unavailable
  }
};

// Injectable for tests — jsdom's window.location.reload is not mockable.
let reloadFn: () => void = () => window.location.reload();
export const setReloadFnForTesting = (fn: () => void): void => {
  reloadFn = fn;
};

const onPreloadError = (event: Event): void => {
  if (!armed) {
    // Post-mount failures (e.g. the lazy video.js chunk) are handled by the
    // component that imported them; a reload here would drop the interview.
    denialReason = 'not-armed';
    return;
  }
  if (reloadInFlight) {
    denialReason = 'in-flight';
    return;
  }
  // Reloading offline swaps the branded error screen for the browser's own
  // network-error page, which the respondent cannot recover from in an embed.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    denialReason = 'offline';
    return;
  }
  // Guard: only ONE guarded reload per session, and none at all when
  // storage is unusable (no way to prove we haven't already reloaded).
  const marker = readMarker();
  if (marker !== null) {
    denialReason = marker === 'unavailable' ? 'storage-unavailable' : 'marker-present';
    return;
  }
  if (!writeMarker()) {
    denialReason = 'storage-unavailable';
    return;
  }

  event.preventDefault();
  reloadInFlight = true;
  denialReason = null;
  Sentry.addBreadcrumb({
    category: 'chunk-reload',
    message: 'Stale chunk detected pre-mount; reloading once',
    level: 'warning',
  });
  try {
    reloadFn();
  } catch {
    // location.reload() can throw in an opaque-origin sandboxed frame. The
    // import already resolved `undefined` (preventDefault above), so the
    // boundary will render — make sure it renders the error screen rather
    // than the in-flight holding screen, and tag why.
    reloadInFlight = false;
    denialReason = 'reload-threw';
  }
};

/**
 * Call once from the app entry, before render. Also reports the outcome
 * of a reload performed by the PREVIOUS document, if any marker is
 * still pending after the guard window.
 */
export const armChunkReload = (): void => {
  armed = true;
  window.addEventListener('vite:preloadError', onPreloadError);
};

/**
 * Call from ChatPage's mount effect. Mounting proves the app booted:
 * disarm the auto-reload (post-mount chunk failures must never reload —
 * they'd destroy the conversation) and, if this load was the product of
 * a guarded reload, clear the marker and report the recovery (sampled —
 * the actionable population is the FAILED path, which the boundary
 * reports unsampled).
 */
export const notifyAppMounted = (): void => {
  armed = false;
  // `reloadInFlight` is deliberately NOT reset: it caps reloads at one
  // per document lifetime. Post-mount it is unreachable anyway (armed is
  // false), but the invariant is one-reload-per-document, not
  // one-per-arming.
  const marker = readMarker();
  if (marker !== null && marker !== 'unavailable') {
    clearMarker();
    if (Math.random() < RECOVERY_SAMPLE_RATE) {
      Sentry.captureMessage('Stale chunk auto-reload recovered', {
        level: 'warning',
        tags: { auto_reload: 'true' },
      });
    }
  }
};

// Test-only reset of module state.
export const resetChunkReloadForTesting = (): void => {
  armed = false;
  reloadInFlight = false;
  denialReason = null;
  reloadFn = () => window.location.reload();
  clearMarker();
};
